import { useState, useCallback } from 'react';

/**
 * Custom hook for managing form data state
 * Handles input changes with type conversion and dirty tracking
 *
 * @param {object} initialData - Initial form data
 * @param {object} options - Configuration options
 * @param {object} options.typeMap - Map of field names to their types ('number', 'boolean', 'date', etc.)
 * @returns {object} Form data, change handler, reset function, and dirty state
 *
 * @example
 * const { formData, handleChange, resetForm, isDirty } = useFormData(
 *   { name: '', age: 0, active: true },
 *   { typeMap: { age: 'number', active: 'boolean' } }
 * );
 */
export const useFormData = (initialData = {}, options = {}) => {
  const { typeMap = {} } = options;
  const [formData, setFormData] = useState(initialData);
  const [isDirty, setIsDirty] = useState(false);

  /**
   * Handle input change with automatic type conversion
   * Works with standard HTML inputs and custom components
   */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    setIsDirty(true);

    setFormData(prev => {
      let processedValue = value;

      // Handle checkbox inputs
      if (type === 'checkbox') {
        processedValue = checked;
      }
      // Apply type conversion based on typeMap or input type
      else if (typeMap[name] === 'number' || type === 'number') {
        processedValue = value === '' ? '' : parseFloat(value);
      } else if (typeMap[name] === 'integer') {
        processedValue = value === '' ? '' : parseInt(value, 10);
      } else if (typeMap[name] === 'boolean') {
        processedValue = value === 'true' || value === true;
      }

      return {
        ...prev,
        [name]: processedValue,
      };
    });
  }, [typeMap]);

  /**
   * Set a specific field value programmatically
   */
  const setFieldValue = useCallback((name, value) => {
    setIsDirty(true);
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  /**
   * Set multiple field values at once
   */
  const setFormValues = useCallback((values) => {
    setIsDirty(true);
    setFormData(prev => ({
      ...prev,
      ...values,
    }));
  }, []);

  /**
   * Reset form to initial data
   */
  const resetForm = useCallback(() => {
    setFormData(initialData);
    setIsDirty(false);
  }, [initialData]);

  /**
   * Reset form to specific data
   */
  const resetFormTo = useCallback((newData) => {
    setFormData(newData);
    setIsDirty(false);
  }, []);

  return {
    formData,
    setFormData,
    handleChange,
    setFieldValue,
    setFormValues,
    resetForm,
    resetFormTo,
    isDirty,
  };
};
